import * as React from 'react'
import { Image, ScrollView, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Button from '@components/button/Button'
import Checkbox from '@components/checkbox/Checkbox'
import Root from '@components/root/Root'
import Header from '@components/header/Header'
import { ModalListService } from '@components/modal/ModalContact'

import useReducer from '@store/useReducer'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as EdlStore from '@store/edl/edl'
import * as ContactsStore from '@store/contacts/contacts'

import scheme from '../scheme'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'

import api from '@api/api'
import Logger from '@utils/logger'

const ViewLayout = ({ title, name, type, form, onSceneChange, onBack, onFinish }: SignatureViewLayoutProps) => {
  const i18n = useI18n()
  const navigation = useNavigation()

  const user = useReducer(UserStore.store, (s) => s.user)
  const site = useReducer(SiteStore.store, (s) => s.site)
  const contacts = useReducer(ContactsStore.store, (s) => s.contacts)

  const [valid, setValid] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [signature, setSignature] = React.useState<FileDetailResponse>()

  React.useEffect(() => {
    scheme.edl
      .validate(form)
      .then(() => setValid(true))
      .catch(() => setValid(false))
  }, [form])

  React.useEffect(() => {
    if (user && user.signatureFileId) {
      api.files.get(user.signatureFileId).then(setSignature).catch(Logger.error)
    }
  }, [user])

  const onSignaturePref = () => {
    EdlStore.actions.setForm({ utilisateurInitiateurSignature: user && user.signatureFileId, usePrefSignature: true })
  }

  const onValidate = () => {
    setLoading(true)
    onFinish().then(() => setLoading(false))
  }

  const links = [
    { label: i18n.t(`screens.site.sites.${site && site.codeLieu}`), onClick: () => navigation.push('/') },
    { label: i18n.t(`screens.edl.title`), onClick: () => navigation.push(`/edl`) },
  ]

  const renderViparis = () => {
    return (
      <>
        <Title>
          {i18n.t('screens.edlSignature.viparisContact', { name: user ? `${user.prenom} ${user.nom}` : '' })}
        </Title>
        <Card>
          {(form.usePrefSignature && signature) || (!form.usePrefSignature && form.utilisateurInitiateurSignature) ? (
            <SignatureContainer
              disabled={loading}
              activeOpacity={0.9}
              onPress={() => onSceneChange('VIPARIS_SIGNATURE')}>
              <Signature
                source={{
                  uri: form.usePrefSignature && signature ? signature.url : form.utilisateurInitiateurSignature,
                }}
                resizeMode="contain"
              />
            </SignatureContainer>
          ) : (
            <Actions>
              <Action>
                <Button
                  color="default"
                  label={i18n.t('screens.edlSignature.manualSignature')}
                  status={loading ? 'disabled' : 'active'}
                  onClick={() => onSceneChange('VIPARIS_SIGNATURE')}
                />
              </Action>
              <Action>
                <Button
                  color="light"
                  label={i18n.t('screens.edlSignature.savedSignature')}
                  status={user && user.signatureFileId && !loading ? 'active' : 'disabled'}
                  onClick={onSignaturePref}
                />
              </Action>
            </Actions>
          )}

          {!!form.utilisateurInitiateurSignature && !form.usePrefSignature && (
            <Checkbox
              label={i18n.t(`screens.edlSignature.signatureToSaved`)}
              disabled={loading}
              onClick={() => EdlStore.actions.setForm({ rememberedSignature: !form.rememberedSignature })}
              value={!!form.rememberedSignature}
            />
          )}
        </Card>
      </>
    )
  }

  const renderOrganizer = () => {
    return (
      <>
        <Title>
          {!!form.contactOrganisateur
            ? i18n.t('screens.edlSignature.organisatorName', {
                name: `${form.contactOrganisateur.prenom} ${form.contactOrganisateur.nom}`,
              })
            : i18n.t('screens.edlSignature.organisator', { name: user ? `${user.prenom} ${user.nom}` : '' })}
        </Title>
        <Card>
          {!form.clientAbsent && form.contactOrganisateur && form.contactOrganisateur.signatureFileUrl ? (
            <SignatureContainer disabled={loading} activeOpacity={0.9} onPress={() => onSceneChange('FORM_SIGNATURE')}>
              <Signature source={{ uri: form.contactOrganisateur.signatureFileUrl }} resizeMode="contain" />
            </SignatureContainer>
          ) : (
            <Actions>
              <Action>
                <Button
                  color="default"
                  label={i18n.t('screens.edlSignature.manualSignature')}
                  status={form.clientAbsent || loading ? 'disabled' : 'active'}
                  onClick={() => onSceneChange('FORM_SIGNATURE')}
                />
              </Action>
            </Actions>
          )}

          <Checkbox
            label={i18n.t(`screens.edlSignature.clientMissing`)}
            disabled={loading}
            onClick={() =>
              EdlStore.actions.setForm({ contactOrganisateur: undefined, clientAbsent: !form.clientAbsent })
            }
            value={!!form.clientAbsent}
          />
        </Card>
      </>
    )
  }

  const onSelectContacts = () => {
    const contactsData = contacts.map((c) => ({ label: `${c.prenom} ${c.nom} (${c.email})`, value: c }))

    const formData = form.contacts
      ? form.contacts.filter((c) => !c.id).map((c) => ({ label: `${c.prenom} ${c.nom} (${c.email})`, value: c }))
      : []

    ModalListService.open({
      data: [...contactsData, ...formData].sort((a, b) => a.label.localeCompare(b.label)),
      onItemSelected: (contacts) => EdlStore.actions.setForm({ contacts }),
      title: i18n.t('screens.edlSignature.contacts.title'),
      value: form.contacts || [],
    })
  }

  return (
    <Root fixed header={title} onLeft={onBack}>
      <ScrollContainer scrollIndicatorInsets={{ right: 1 }} bounces={false} keyboardShouldPersistTaps="handled">
        <Header
          title={i18n.t('screens.edlSignature.stepTitle', { type: i18n.t(`screens.edlRecap.${type}`) })}
          links={links}
          description={i18n.t('screens.edlSignature.description', { type: i18n.t(`screens.edlRecap.${type}`) })}
        />

        <MainContainer>
          <Line>
            <Column>{renderViparis()}</Column>
            <Column>{renderOrganizer()}</Column>
          </Line>

          <ContactsContainer>
            <Line>
              <ContactsInfos>
                <ContactsInfo>{i18n.t('screens.edlSignature.contacts.sendToContacts')}</ContactsInfo>
                <ContactsCount>
                  {form.contacts && form.contacts.length > 0
                    ? i18n.t('screens.edlSignature.contacts.count', { count: form.contacts.length })
                    : i18n.t('screens.edlSignature.contacts.noContact')}
                </ContactsCount>
              </ContactsInfos>
              <Button label={i18n.t('common.select')} status="active" onClick={onSelectContacts} />
            </Line>
          </ContactsContainer>
        </MainContainer>
      </ScrollContainer>

      <Buttons>
        <ButtonContainer>
          <Button
            textOnly
            textColor={theme.colors.primary}
            label={i18n.t('screens.edl.spaces.back')}
            status={loading ? 'disabled' : 'active'}
            onClick={onBack}
          />
        </ButtonContainer>
        <ButtonContainer>
          <Button
            label={i18n.t('common.finish')}
            status={loading ? 'loading' : valid ? 'active' : 'disabled'}
            onClick={onValidate}
          />
        </ButtonContainer>
      </Buttons>
    </Root>
  )
}

export default ViewLayout

const ScrollContainer = styled(ScrollView)`
  flex: 1;
  flex-direction: column;
`

const MainContainer = styled(View)`
  margin: 24px;
  padding: 24px 12px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  ${(props) => props.theme.constants.shadow};
`

const Line = styled(View)`
  flex-direction: row;
`

const Column = styled(View)`
  flex: 1;
  flex-direction: column;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title1};
  font-size: 24px;
  text-align: center;
  margin-bottom: 16px;
  margin-top: 24px;
`

const Card = styled(View)`
  margin: 0 12px;
  padding: 10px 20px;
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
  flex-grow: 1;
  ${(props) => props.theme.constants.shadow};
`

const SignatureContainer = styled(TouchableOpacity)`
  height: 300px;
`

const Signature = styled(Image)`
  height: 300px;
`

const Actions = styled(View)`
  height: 300px;
  align-items: center;
  justify-content: center;
`

const Action = styled(View)`
  width: 270px;
  margin: 8px;
`

const Buttons = styled(View)`
  flex-direction: row;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  width: 275px;
  margin: 12px;
`

const ContactsContainer = styled(View)`
  margin-top: 24px;
  padding: 0 12px;
`

const ContactsInfos = styled(View)`
  flex: 1;
  margin-right: 24px;
  justify-content: center;
`

const ContactsInfo = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
`

const ContactsCount = styled(Text)`
  margin-top: 8px;
  ${(props) => props.theme.fonts.Footnote};
`
